/**
 * Module dependencies.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, ChevronsUpDown, Database, LogOut, User } from 'lucide-react';
import { Avatar } from 'app/components/ui/avatar';
import { cn } from 'app/lib/utils';
import { appRoutes } from 'app/routes';
import { useLogout } from 'app/hooks/requests/use-logout';
import { useMe } from 'app/hooks/requests/use-me';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from 'app/components/ui/alert-dialog';

/**
 * `Props` type.
 */

type Props = {
  name: string;
  email: string;
  initials: string;
  photo?: string | null;
};

/**
 * `MenuLink` component.
 */

function MenuLink({
  to,
  icon,
  label,
  onClick
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <Link
      className={
        'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:bg-muted [&_svg]:h-4 [&_svg]:w-4 [&_svg]:text-muted-foreground'
      }
      onClick={onClick}
      to={to}
    >
      {icon}

      <span>{label}</span>
    </Link>
  );
}

/**
 * Export `UserMenu` component.
 */

export function UserMenu({ name, email, initials, photo }: Props): JSX.Element {
  const [translate] = useTranslation();
  const [open, setOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { mutate: logout, isLoading } = useLogout();
  const { data: me } = useMe();
  const isAdmin = !!me?.isAdmin;

  useEffect(() => {
    if (!open) {
      return;
    }

    function onPointerDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className={'relative'} ref={containerRef}>
      {open && (
        <div
          className={
            'absolute bottom-full left-0 right-0 mb-2 rounded-lg border border-border bg-popover p-1 shadow-lg'
          }
        >
          <div className={'border-b border-border px-2.5 py-2'}>
            <div className={'truncate text-sm font-semibold'}>{name}</div>

            <div className={'truncate text-xs text-muted-foreground'}>{email}</div>
          </div>

          <div className={'py-1'}>
            <MenuLink
              icon={<User />}
              label={translate('settings.menu.profile')}
              onClick={() => setOpen(false)}
              to={appRoutes.dashboard.settings.profile}
            />

            <MenuLink
              icon={<Bell />}
              label={translate('menu.notifications')}
              onClick={() => setOpen(false)}
              to={appRoutes.dashboard.settings.notifications}
            />

            {isAdmin && (
              <MenuLink
                icon={<Database />}
                label={translate('settings.menu.backups')}
                onClick={() => setOpen(false)}
                to={appRoutes.dashboard.settings.backups}
              />
            )}
          </div>

          <div className={'border-t border-border pt-1'}>
            <button
              className={
                'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-destructive outline-none transition-colors hover:bg-destructive/10 focus-visible:bg-destructive/10 [&_svg]:h-4 [&_svg]:w-4'
              }
              onClick={() => {
                setOpen(false);
                setConfirmLogout(true);
              }}
              type={'button'}
            >
              <LogOut />

              <span>{translate('common.actions.logout')}</span>
            </button>
          </div>
        </div>
      )}

      <button
        className={cn(
          'flex w-full items-center gap-2.5 rounded-md p-2 text-left outline-none transition-colors hover:bg-card/60 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
          open && 'bg-card shadow-sm'
        )}
        onClick={() => setOpen(value => !value)}
        type={'button'}
      >
        <Avatar className={'h-8 w-8 text-xs'} initials={initials} src={photo} />

        <div className={'min-w-0 flex-1'}>
          <div className={'truncate text-[13px] font-semibold leading-tight'}>{name}</div>

          <div className={'truncate text-[11px] text-muted-foreground'}>{email}</div>
        </div>

        <ChevronsUpDown className={'h-4 w-4 shrink-0 text-muted-foreground'} />
      </button>

      <AlertDialog onOpenChange={setConfirmLogout} open={confirmLogout}>
        <AlertDialogContent className={'max-w-sm'}>
          <AlertDialogHeader>
            <AlertDialogTitle>{translate('menu.logoutTitle')}</AlertDialogTitle>

            <AlertDialogDescription>{translate('menu.logoutMessage')}</AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>{translate('common.actions.cancel')}</AlertDialogCancel>

            <AlertDialogAction
              className={'bg-destructive text-destructive-foreground hover:bg-destructive/90'}
              disabled={isLoading}
              onClick={() => logout()}
            >
              {translate('common.actions.logout')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
